import { useState } from "react";
import { Megaphone, Download, Loader2, ChevronDown, ExternalLink } from "lucide-react";
import { API } from "../lib/api";

const GUIDE_STEPS = [
  { title: "Téléchargez votre visuel", desc: "Choisissez le format selon le réseau : carré pour le fil Facebook/Instagram, story pour les stories, paysage pour un lien partagé." },
  { title: "Ouvrez le Gestionnaire de publicités", desc: "Rendez-vous sur facebook.com/business/ads-manager et cliquez sur « Créer ». Choisissez l'objectif « Trafic » pour amener des visiteurs sur votre page artiste." },
  { title: "Importez le visuel", desc: "Dans la section « Contenu publicitaire », ajoutez l'image téléchargée. Titre conseillé : votre nom d'artiste + « musiques en licence »." },
  { title: "Collez votre lien de partage", desc: "Utilisez le lien de votre page artiste (boutons de partage sur votre page publique) : la publication affichera automatiquement votre visuel personnalisé." },
  { title: "Ciblez et publiez", desc: "Définissez un budget quotidien (5 € suffisent pour débuter), ciblez les créateurs de contenu, réalisateurs et marques, puis publiez." },
];

const FORMATS = [
  { key: "carre", label: "Post carré", dims: "1080 × 1080", hint: "Facebook · Instagram", aspect: "aspect-square" },
  { key: "story", label: "Story", dims: "1080 × 1920", hint: "Instagram · Facebook", aspect: "aspect-[9/16]" },
  { key: "paysage", label: "Post paysage", dims: "1200 × 628", hint: "Facebook · LinkedIn · X", aspect: "aspect-[1200/628]" },
];

export const StudioPromo = ({ artistName }) => {
  const [loading, setLoading] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);

  const download = async (fmt) => {
    setLoading(fmt);
    try {
      const res = await fetch(`${API}/studio/promo/${fmt}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `promo_${fmt}_${(artistName || "artiste").replace(/\s+/g, "_").toLowerCase()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* silencieux : l'aperçu reste visible */
    } finally {
      setLoading("");
    }
  };

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="studio-promo-section">
      <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
        <Megaphone className="h-5 w-5 text-amber-500" /> Visuels promotionnels
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">
        Des visuels à votre nom, prêts à publier sur les réseaux sociaux pour annoncer que vos
        musiques sont disponibles en licence. Cliquez pour télécharger, puis partagez.
      </p>
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
        {FORMATS.map((f) => (
          <div key={f.key} className="border border-white/[0.06] p-4" data-testid={`promo-card-${f.key}`}>
            <div className={`${f.aspect} w-full overflow-hidden border border-white/10 bg-black`}>
              <img
                src={`${API}/studio/promo/${f.key}`}
                alt={`Visuel ${f.label}`}
                data-testid={`promo-preview-${f.key}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <p className="mt-3 text-sm font-medium text-zinc-100">{f.label}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              {f.dims} — {f.hint}
            </p>
            <button
              onClick={() => download(f.key)}
              disabled={loading === f.key}
              data-testid={`promo-download-${f.key}`}
              className="mt-3 flex w-full items-center justify-center gap-2 border border-amber-500/50 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
            >
              {loading === f.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Télécharger
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 border border-white/[0.06]">
        <button
          onClick={() => setGuideOpen((o) => !o)}
          data-testid="promo-guide-toggle"
          className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-white/[0.02]"
        >
          <span className="text-[11px] uppercase tracking-[0.25em] text-amber-400">
            Comment lancer votre pub Facebook ?
          </span>
          <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${guideOpen ? "rotate-180" : ""}`} />
        </button>
        {guideOpen && (
          <ol className="space-y-4 border-t border-white/[0.06] px-5 py-5" data-testid="promo-guide">
            {GUIDE_STEPS.map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center border border-amber-500/40 font-display text-xs font-bold text-amber-400">
                  {i + 1}
                </span>
                <div>
                  <p className="text-sm font-medium text-zinc-100">{s.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-zinc-500">{s.desc}</p>
                </div>
              </li>
            ))}
            <li>
              <a
                href="https://www.facebook.com/business/ads-manager"
                target="_blank" rel="noopener noreferrer"
                data-testid="promo-guide-link"
                className="mt-2 inline-flex items-center gap-2 border border-amber-500/50 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
              >
                Ouvrir le Gestionnaire de publicités <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </li>
          </ol>
        )}
      </div>
    </section>
  );
};
